import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "./AuthContext";
import { Star } from "lucide-react";

import { BACKEND_URL as GREENWICH_BACKEND_URL } from '../utils/network.js';
import { toast } from '../utils/toast.js';
function OrganizationAccountProfile() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, updateUserInterests, updateUserProfile } = useAuth();
  
  const initialInterests = location.state?.selectedInterests || user?.interests || [];
  
  const [profileImage, setProfileImage] = useState(user?.profileImage || "/nkarner/default-avatar.jpg");
  const [coverImage, setCoverImage] = useState(user?.coverImage || "/nkarner/default-cover.jpg");
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [coverImageFile, setCoverImageFile] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [userInterests, setUserInterests] = useState(initialInterests);
  const [isEditingInterests, setIsEditingInterests] = useState(false);
  const [userInfo, setUserInfo] = useState({
    name: user?.organizationName || user?.name || "",
    surname: user?.surname || "",
    title: user?.title || "",
    location: user?.location || "",
    about: user?.about || user?.description || "",
    email: user?.email || "",
    phone: user?.phone || "",
    website: user?.website || ""
  });
  
  const [posts, setPosts] = useState([]);
  const [newPost, setNewPost] = useState({
    content: "",
    image: null
  });
  const [followersCount, setFollowersCount] = useState(0);
  const [followingCount, setFollowingCount] = useState(0);

  // Update user info when user data changes
  useEffect(() => {
    if (user) {
      setUserInfo(prev => ({
        ...prev,
        name: user.organizationName || user.name || "",
        surname: user.surname || "",
        email: user.email || "",
        phone: user.phone || "",
        title: user.title || "",
        location: user.location || "",
        about: user.about || user.description || "",
        website: user.website || ""
      }));
      setProfileImage(user.profileImage || "/nkarner/default-avatar.jpg");
      setCoverImage(user.coverImage || "/nkarner/default-cover.jpg");
      loadFollowStats();
    }
  }, [user]);

  const loadFollowStats = async () => {
    try {
      if (!user?._id) return;
      const token = localStorage.getItem('token');
      if (!token) return;

      // Load followers and following
      const [followersRes, followingRes] = await Promise.all([
        fetch(`${GREENWICH_BACKEND_URL}/api/users/${user._id}/followers`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${GREENWICH_BACKEND_URL}/api/users/${user._id}/following`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (followersRes.ok) {
        const followersData = await followersRes.json();
        setFollowersCount(followersData.followers?.length || 0);
      }

      if (followingRes.ok) {
        const followingData = await followingRes.json();
        setFollowingCount(followingData.following?.length || 0);
      }
    } catch (error) {
      console.error('Error loading follow stats:', error);
      // Fallback to user data if available
      if (user?.followers) setFollowersCount(user.followers.length || 0);
      if (user?.following) setFollowingCount(user.following.length || 0);
    }
  };

  const allInterests = [
    "Environment", "Education", "Technology", "Health",
    "Arts & Culture", "Community Development", "Human Rights",
    "Social Inclusion", "International Exchanges", "Entrepreneurship"
  ];
  const interestKeyMap = {
    "Environment": "environment", "Education": "education", "Technology": "technology", "Health": "health",
    "Arts & Culture": "artsCulture", "Community Development": "communityDevelopment", "Human Rights": "humanRights",
    "Social Inclusion": "socialInclusion", "International Exchanges": "internationalExchanges", "Entrepreneurship": "entrepreneurship",
  };
  const trInterest = (name) => t(`orgAccountProfile.interests.${interestKeyMap[name] || name}`, name);

  const handleProfileImageUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      setProfileImageFile(file);
      const imageUrl = URL.createObjectURL(file);
      setProfileImage(imageUrl);
      setIsSaving(true);
      // Auto-save profile image
      if (updateUserProfile) {
        try {
          await updateUserProfile({ profileImage: imageUrl });
        } finally {
          setIsSaving(false);
          setProfileImageFile(null);
        }
      } else {
        setIsSaving(false);
        setProfileImageFile(null);
      }
    }
  };

  // ✅ Get full image URL helper
  const getImageUrl = (imagePath, defaultImage) => {
    if (!imagePath) return defaultImage || '/nkarner/default-avatar.jpg';
    
    // If it's a blob URL (just uploaded), return as is
    if (imagePath.startsWith('blob:') || imagePath.startsWith('data:')) {
      return imagePath;
    }
    
    // If already full URL, return as is
    if (imagePath.startsWith('http')) {
      return imagePath;
    }
    
    // Backend uploads usually come as "/uploads/..." or "uploads/..."
    if (imagePath.includes('/uploads/') || imagePath.includes('uploads/')) {
      const normalized = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
      return `${GREENWICH_BACKEND_URL}${normalized}`;
    }
    
    // If it starts with /, prepend backend URL
    if (imagePath.startsWith('/')) {
      return `${GREENWICH_BACKEND_URL}${imagePath}`;
    }
    
    // Default fallback
    return defaultImage || '/nkarner/default-avatar.jpg';
  };

  const handleCoverImageUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      setCoverImageFile(file);
      const imageUrl = URL.createObjectURL(file);
      setCoverImage(imageUrl);
      setIsSaving(true);
      // Auto-save cover image
      if (updateUserProfile) {
        try {
          await updateUserProfile({ coverImage: imageUrl });
        } finally {
          setIsSaving(false);
          setCoverImageFile(null);
        }
      } else {
        setIsSaving(false);
        setCoverImageFile(null);
      }
    }
  };

  const toggleInterest = async (interestName) => {
    const newInterests = userInterests.includes(interestName) 
      ? userInterests.filter(item => item !== interestName)
      : [...userInterests, interestName];
    
    setUserInterests(newInterests);
    
    // Auto-save interests
    if (updateUserInterests) {
      await updateUserInterests(newInterests);
    }
  };

  const handleInputChange = async (field, value) => {
    setUserInfo(prev => ({
      ...prev,
      [field]: value
    }));

    // Auto-save to backend after 1 second delay
    if (updateUserProfile && user) {
      setTimeout(async () => {
        await updateUserProfile({ [field]: value });
      }, 1000);
    }
  };

  const handleSaveProfile = async () => {
    // Update user interests in the global state
    if (updateUserInterests) {
      await updateUserInterests(userInterests);
    }

    // Update user profile data
    if (updateUserProfile) {
      const profileData = {
        title: userInfo.title,
        location: userInfo.location,
        about: userInfo.about,
        website: userInfo.website,
        profileImage: profileImage,
        coverImage: coverImage
      };
      await updateUserProfile(profileData);
    }
    
    console.log("Organization profile saved:", { ...userInfo, interests: userInterests });
    navigate("/organization-dashboard"); // Տանում է organization dashboard
  };

  const handleEditInterests = () => {
    setIsEditingInterests(true);
  };

  const handleSaveInterests = async () => {
    setIsEditingInterests(false);
    // Save interests when exiting edit mode
    if (updateUserInterests) {
      await updateUserInterests(userInterests);
    }
  };

  // Posts functionality
  const handlePostInputChange = (field, value) => {
    setNewPost(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePostImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const imageUrl = URL.createObjectURL(file);
      setNewPost(prev => ({
        ...prev,
        image: imageUrl,
        // Keep the real File around too — the preview URL above is only a local
        // blob: reference and can't be uploaded to the backend on its own.
        imageFile: file
      }));
    }
  };

  const handleCreatePost = async () => {
    if (newPost.content.trim() || newPost.image) {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          toast(t('orgAccountProfile.alertPleaseLogin'));
          return;
        }

        console.log('🔄 Creating post...');

        // The backend's POST /api/posts route parses uploaded images via
        // multer (upload.array('images', 10)), so an attached photo has to be
        // sent as multipart/form-data — a JSON body with a blob: URL string
        // never reaches the server as an actual file.
        let response;
        if (newPost.imageFile) {
          const formData = new FormData();
          formData.append('content', newPost.content);
          formData.append('images', newPost.imageFile);
          response = await fetch(`${GREENWICH_BACKEND_URL}/api/posts`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`
            },
            body: formData
          });
        } else {
          response = await fetch(`${GREENWICH_BACKEND_URL}/api/posts`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              content: newPost.content
            })
          });
        }

        console.log('📨 Response status:', response.status);
        
        const contentType = response.headers.get('content-type');
        console.log('📄 Content-Type:', contentType);

        const text = await response.text();
        console.log('📝 Raw response:', text.substring(0, 200));

        let data;
        try {
          data = JSON.parse(text);
        } catch (parseError) {
          console.error('❌ JSON parse error:', parseError);
          console.error('📝 Full response:', text);
          toast(t('orgAccountProfile.alertInvalidResponse'));
          return;
        }

        if (data.success) {
          console.log('✅ Post created successfully:', data.post);
          setPosts(prev => [data.post, ...prev]);
          setNewPost({
            content: "",
            image: null,
            imageFile: null
          });
          toast(t('orgAccountProfile.alertPostSuccess'));
        } else {
          console.error('❌ API error:', data.error);
          toast(t('orgAccountProfile.alertErrorCreating') + data.error);
        }
      } catch (error) {
        console.error('❌ Network error:', error);
        toast(t('orgAccountProfile.alertNetworkError') + error.message);
      }
    } else {
      toast(t('orgAccountProfile.alertEnterContent'));
    }
  };

  const handleLikePost = (postId) => {
    setPosts(prev => prev.map(post => 
      post.id === postId ? { ...post, likes: post.likes + 1 } : post
    ));
  };

  return (
    <div className="account-profile-page">
      {/* Header Section */}
      <div className="profile-header">
        <div className="cover-container">
          <div className="cover-photo">
            <img 
              src={getImageUrl(coverImage, '/nkarner/default-cover.jpg')}
              alt={t('orgAccountProfile.coverAlt')}
              onError={(e) => {
                e.target.src = '/nkarner/default-cover.jpg';
              }}
            />
            <div className="cover-overlay"></div>
          </div>
          <div className="cover-actions">
            <input
              type="file"
              accept="image/*"
              onChange={handleCoverImageUpload}
              className="cover-upload-input"
              id="cover-upload"
            />
            <label htmlFor="cover-upload" className="cover-action-btn">
              <span className="icon"></span>
              {t('orgAccountProfile.editCover')}
            </label>
          </div>
        </div>

        {/* Profile Info Section */}
        <div className="profile-info-container">
          <div className="avatar-section">
            <div className="avatar-wrapper" style={{ position: 'relative' }}>
              <img 
                src={getImageUrl(profileImage, '/nkarner/default-avatar.jpg')} 
                className="profile-avatar"
                alt={t('orgAccountProfile.profileAlt')}
                onError={(e) => {
                  e.target.src = '/nkarner/default-avatar.jpg';
                }}
              />
              {/* Verified Badge - Only show if verified and NOT in edit mode */}
              {user?.isVerified && !isSaving && !profileImageFile && !coverImageFile && (
                <Star 
                  style={{
                    position: 'absolute',
                    bottom: '10px',
                    right: '10px',
                    width: '32px',
                    height: '32px',
                    color: '#FFD700',
                    fill: '#FFD700',
                    backgroundColor: 'white',
                    borderRadius: '50%',
                    padding: '4px',
                    zIndex: 10,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                    pointerEvents: 'none'
                  }}
                  title={t('orgAccountProfile.verifiedOrgTitle')}
                />
              )}
              <input
                type="file"
                accept="image/*"
                onChange={handleProfileImageUpload}
                className="avatar-upload-input"
                id="avatar-upload"
              />
              <label htmlFor="avatar-upload" className="avatar-upload-btn">
                <img src="/nkarner/photo-camera.png" alt={t('orgAccountProfile.uploadAlt')} className="camera-icon" />
            </label>

            </div>
          </div>

          <div className="profile-main-info">
            <div className="name-section">
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%' }}>
                <input
                  type="text"
                  placeholder={t('orgAccountProfile.placeholderOrgName')}
                  value={userInfo.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className="name-input"
                  style={{ flex: 1 }}
                />
                {user?.isVerified && (
                  <Star 
                    size={28}
                    style={{
                      color: '#FFD700',
                      fill: '#FFD700',
                      flexShrink: 0
                    }}
                    title={t('orgAccountProfile.verifiedOrgTitle')}
                  />
                )}
              </div>
              {/* Followers/Following Stats */}
              <div style={{
                display: 'flex',
                gap: '20px',
                marginTop: '15px',
                paddingTop: '15px',
                borderTop: '1px solid #e0e0e0'
              }}>
                <div style={{ textAlign: 'center', cursor: 'pointer' }}>
                  <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#2d5016' }}>
                    {followersCount}
                  </div>
                  <div style={{ fontSize: '14px', color: '#666' }}>{t('orgAccountProfile.followers')}</div>
                </div>
                <div style={{ textAlign: 'center', cursor: 'pointer' }}>
                  <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#2d5016' }}>
                    {followingCount}
                  </div>
                  <div style={{ fontSize: '14px', color: '#666' }}>{t('orgAccountProfile.following')}</div>
                </div>
              </div>
              <input
                type="text"
                placeholder={t('orgAccountProfile.placeholderOrgType')}
                value={userInfo.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                className="title-input"
              />
            </div>

            <div className="location-contact">
              <input
                type="text"
                placeholder={t('orgAccountProfile.placeholderLocation')}
                value={userInfo.location}
                onChange={(e) => handleInputChange('location', e.target.value)}
                className="location-input"
              />
              <div className="contact-badges">
                <div className="contact-badge">
                  <span className="badge-icon"></span>
                  <input
                    type="email"
                    placeholder={t('orgAccountProfile.placeholderEmail')}
                    value={userInfo.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className="contact-input"
                  />
                </div>
                <div className="contact-badge">
                  <span className="badge-icon"></span>
                  <input
                    type="tel"
                    placeholder={t('orgAccountProfile.placeholderPhone')}
                    value={userInfo.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    className="contact-input"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="profile-content-wrapper">
        <div className="content-grid">
          {/* Left Sidebar */}
          <div className="sidebar-column">
            {/* Quick Actions Section */}
            <div className="profile-section">
              <div className="section-header">
                <h3>{t('orgAccountProfile.quickActions')}</h3>
              </div>
              <div className="quick-actions-list" style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '10px'
              }}>
                <button 
                  className="quick-action-button"
                  onClick={() => window.location.href = '/organization-activities?tab=posts'}
                  style={{
                    padding: '12px',
                    background: '#f8f9fa',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    textAlign: 'left',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    transition: 'all 0.3s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#e9ecef'}
                  onMouseLeave={(e) => e.currentTarget.style.background = '#f8f9fa'}
                >
                  <span style={{ fontSize: '20px' }}>📝</span>
                  <span>{t('orgAccountProfile.myPosts')}</span>
                </button>
                <button 
                  className="quick-action-button"
                  onClick={() => window.location.href = '/organization-activities?tab=liked'}
                  style={{
                    padding: '12px',
                    background: '#f8f9fa',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    textAlign: 'left',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    transition: 'all 0.3s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#e9ecef'}
                  onMouseLeave={(e) => e.currentTarget.style.background = '#f8f9fa'}
                >
                  <span style={{ fontSize: '20px' }}>❤️</span>
                  <span>{t('orgAccountProfile.likedPosts')}</span>
                </button>
                <button 
                  className="quick-action-button"
                  onClick={() => window.location.href = '/organization-activities?tab=saved'}
                  style={{
                    padding: '12px',
                    background: '#f8f9fa',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    textAlign: 'left',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    transition: 'all 0.3s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#e9ecef'}
                  onMouseLeave={(e) => e.currentTarget.style.background = '#f8f9fa'}
                >
                  <span style={{ fontSize: '20px' }}>💾</span>
                  <span>{t('orgAccountProfile.savedPosts')}</span>
                </button>
              </div>
            </div>

            {/* About Section */}
            <div className="profile-section">
              <div className="section-header">
                <h3>{t('orgAccountProfile.aboutOrg')}</h3>
                <div className="section-icon"></div>
              </div>
              <textarea
                placeholder={t('orgAccountProfile.aboutPlaceholder')}
                value={userInfo.about}
                onChange={(e) => handleInputChange('about', e.target.value)}
                className="about-textarea"
                rows="5"
              />
            </div>

            {/* Interests Section */}
            <div className="profile-section">
              <div className="section-header">
                <h3>{t('orgAccountProfile.areasOfInterest')}</h3>
                <div className="section-actions">
                  {!isEditingInterests ? (
                    <button className="action-btn edit-btn" onClick={handleEditInterests}>
                      {t('orgAccountProfile.edit')}
                    </button>
                  ) : (
                    <button className="action-btn save-btn" onClick={handleSaveInterests}>
                      {t('orgAccountProfile.save')}
                    </button>
                  )}
                </div>
              </div>

              {isEditingInterests ? (
                <div className="interests-edit-panel">
                  <p className="edit-guide">{t('orgAccountProfile.selectInterestsGuide')}</p>
                  <div className="interests-grid-edit">
                    {allInterests.map((interest) => (
                      <label key={interest} className="interest-option">
                        <input
                          type="checkbox"
                          checked={userInterests.includes(interest)}
                          onChange={() => toggleInterest(interest)}
                          className="interest-checkbox-input"
                        />
                        <span className="interest-checkbox"></span>
                        <span className="interest-label">{trInterest(interest)}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="interests-display">
                  {userInterests.length > 0 ? (
                    <div className="interests-tags">
                      {userInterests.map((interest) => (
                        <span key={interest} className="interest-tag">
                          {trInterest(interest)}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="empty-state">{t('orgAccountProfile.noInterestsYet')}</p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Main Column */}
          <div className="main-column">
            {/* Create Post Section */}
            <div className="profile-section">
              <div className="section-header">
                <h3>{t('orgAccountProfile.createPost')}</h3>
                <div className="section-icon"></div>
              </div>
              <div className="create-post-form">
                <textarea
                  placeholder={t('orgAccountProfile.postPlaceholder')}
                  value={newPost.content}
                  onChange={(e) => handlePostInputChange('content', e.target.value)}
                  className="post-textarea"
                  rows="3"
                />
                <div className="post-actions">
                  <div className="post-media-actions">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handlePostImageUpload}
                      className="media-upload-input"
                      id="media-upload"
                    />
                    <label htmlFor="media-upload" className="media-action-btn">
                      <span className="icon"></span>
                      {t('orgAccountProfile.addPhoto')}
                    </label>
                  </div>
                  <button
                    className="post-submit-btn"
                    onClick={handleCreatePost}
                    disabled={!newPost.content.trim() && !newPost.image}
                  >
                    {t('orgAccountProfile.post')}
                  </button>
                </div>
                {newPost.image && (
                  <div className="post-preview">
                    <img src={newPost.image} alt={t('orgAccountProfile.postPreviewAlt')} className="post-preview-image" />
                    <button 
                      className="remove-preview-btn"
                      onClick={() => setNewPost(prev => ({ ...prev, image: null, imageFile: null }))}
                    >
                      ×
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Posts Section */}
            <div className="profile-section">
              <div className="section-header">
                <h3>{t('orgAccountProfile.orgPosts')}</h3>
                <div className="section-icon"></div>
              </div>
              <div className="posts-feed">
                {posts.length > 0 ? (
                  posts.map(post => (
                    <div key={post.id} className="post-item">
                      <div className="post-header">
                        <img 
                          src={getImageUrl(profileImage, '/nkarner/default-avatar.jpg')}
                          alt={t('orgAccountProfile.profileAlt')}
                          className="post-author-avatar"
                          onError={(e) => {
                            e.target.src = '/nkarner/default-avatar.jpg';
                          }}
                        />
                        <div className="post-author-info">
                          <div className="post-author-name">{userInfo.name || t('orgAccountProfile.defaultOrgFallback')}</div>
                          <div className="post-time">{post.timestamp}</div>
                        </div>
                      </div>
                      <div className="post-content">
                        {post.content && <p>{post.content}</p>}
                        {post.image && (
                          <div className="post-image-container">
                            <img 
                              src={getImageUrl(post.image, '/nkarner/default-avatar.jpg')}
                              alt={t('orgAccountProfile.postAlt')}
                              className="post-image"
                              onError={(e) => {
                                e.target.src = '/nkarner/default-avatar.jpg';
                              }}
                            />
                          </div>
                        )}
                      </div>
                      
                    </div>
                  ))
                ) : (
                  <div className="empty-posts-state">
                    <p>{t('orgAccountProfile.noPostsYet')}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Experience Section */}
            <div className="profile-section">
              <div className="section-header">
                <h3>{t('orgAccountProfile.orgActivities')}</h3>
                <div className="section-icon"></div>
              </div>
              <div className="experience-form">
                <div className="form-row">
                  <input
                    type="text"
                    placeholder={t('orgAccountProfile.activityNamePlaceholder')}
                    className="experience-input"
                  />
                  <input
                    type="text"
                    placeholder={t('orgAccountProfile.partnerOrgPlaceholder')}
                    className="experience-input"
                  />
                </div>
                <textarea
                  placeholder={t('orgAccountProfile.activityDescPlaceholder')}
                  className="experience-textarea"
                  rows="3"
                />
                <button className="add-experience-btn">
                  <span className="icon">+</span>
                  {t('orgAccountProfile.addActivity')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer Actions */}
      <div className="profile-actions-footer">
        <button className="secondary-btn" onClick={() => navigate("/organization-dashboard")}>
          {t('orgAccountProfile.previewProfile')}
        </button>
        <button className="primary-btn" onClick={handleSaveProfile}>
          {t('orgAccountProfile.saveContinue')}
        </button>
      </div>
    </div>
  );
}
export default OrganizationAccountProfile;

